import { App } from '@ps20-frontend/frontend-models';
import { Feature } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { IAppState } from '../localStore';
import { selectExistsJHPOnly } from './beginnAblaufdatum.selector';

const HINT_KEY = 'beginnundablauf.mindestlaufzeit.hinweis';

/** At least one product replaces a previous contract (Beibehalten oder Umstellen). */
const hasReplacement = (state: IAppState): boolean =>
  state.produkte.some(p => p.vorvertragsnummer?.length > 0);

/** At least one product is "Beibehalten" (retention). */
const hasRetention = (state: IAppState): boolean =>
  state.produkte.some(p => p.beibehalten);

/** At least one non-JHP product is new business (no Vorvertrag). */
const hasNewBusiness = (state: IAppState): boolean =>
  state.produkte.some(p => !(p.vorvertragsnummer?.length > 0) && p.appId !== App.JHP);

/** Multiple distinct Hauptfälligkeiten (MM-DD) across all Vorverträge. */
const hasDifferentDueDates = (state: IAppState): boolean => {
  const fromVorversicherung = (state.vorversicherungen ?? [])
    .flatMap(v => v.vorversicherungsListe ?? [])
    .map(i => i.ablaufdatum?.trim())
    .filter(Boolean)
    .map(d => dayjs(d, DATEFORMAT_LOCAL_STATE))
    .filter(d => d.isValid());
  const fromVorbelegung = (state.produkte ?? [])
    .flatMap(p => p.vorvertragsnummer ?? [])
    .map(vn => vn.vorbelegungForBeibehaltung?.ablaufDatum?.trim())
    .filter(Boolean)
    .map(d => dayjs(d))
    .filter(d => d.isValid());
  const mmDDSet = new Set(
    [...fromVorversicherung, ...fromVorbelegung].map(d => d.format('DD.MM.')),
  );
  return mmDDSet.size > 1;
};

const isFeatureToggleOn = (state: IAppState): boolean =>
  !!state.transientState?.featureToggles?.[Feature.MINDESTLAUFZEIT];

/**
 * Checkbox is selectable only for pure Ersatz/Beibehaltung without due-date conflicts.
 * Any new business or differing Hauptfälligkeiten disables it.
 */
const isMindestlaufzeitCheckboxSelectable = (state: IAppState): boolean =>
  hasReplacement(state) && !hasNewBusiness(state) && !hasDifferentDueDates(state);

/**
 * Mindestvertragslaufzeit is active: the checkbox is selectable, the feature toggle
 * is on, and the user has checked it. When active, HF locks to the Vorvertrag's
 * Hauptfälligkeit and each produkt.ablauf is recomputed (see mindestvertragslaufzeit.util).
 */
export const selectIsMindestvertragslaufzeitActive = (state: IAppState): boolean =>
  isMindestlaufzeitCheckboxSelectable(state) &&
  isFeatureToggleOn(state) &&
  !!state.mindestvertragslaufzeit;

export const selectShowMindestvertragslaufzeitFieldset = (state: IAppState): boolean =>
  hasReplacement(state) && !selectExistsJHPOnly(state);

export const selectIsMindestvertragslaufzeitCheckboxChecked = (state: IAppState): boolean =>
  selectIsMindestvertragslaufzeitActive(state);

export const selectIsMindestvertragslaufzeitCheckboxDisabled = (state: IAppState): boolean =>
  hasReplacement(state) && !isMindestlaufzeitCheckboxSelectable(state);

export const selectMindestvertragslaufzeitHinweise = (state: IAppState): string[] => {
  if (!hasReplacement(state)) {
    return [];
  }
  const retention = hasRetention(state);
  const newBusiness = hasNewBusiness(state);
  const differentDueDates = hasDifferentDueDates(state);
  const checkboxSelectable = !newBusiness && !differentDueDates;
  if (checkboxSelectable && isFeatureToggleOn(state) && !!state.mindestvertragslaufzeit) {
    return [`${HINT_KEY}.mindestlaufzeit`];
  }
  const hints: string[] = [];
  if (retention) {
    hints.push(`${HINT_KEY}.beibehaltung`);
  }
  if (newBusiness) {
    hints.push(`${HINT_KEY}.neugeschaeft`);
  }
  if (differentDueDates) {
    hints.push(`${HINT_KEY}.diffHF`);
  }
  return hints;
};

export const selectIsHauptfaelligkeitLockedByMindestlaufzeit = (state: IAppState): boolean => {
  if (!isFeatureToggleOn(state)) {
    return hasRetention(state);
  }
  return selectIsMindestvertragslaufzeitActive(state) || hasRetention(state);
};
